import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
} from "@mui/material";
import {
  Logout as LogoutIcon,
  DeleteForever as DeleteIcon,
  FileDownload as ExportIcon,
} from "@mui/icons-material";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@context/NotificationContext";
import { useLocalKeys } from "@hooks/useLocalKeys";
import { routes } from "@/constants/routes";

export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, deleteAccount } = useAuth();
  const { notify } = useNotification();
  const { exportBundle } = useLocalKeys();

  const [ackChecked, setAckChecked] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate(routes.home);
    } catch {
      notify(t("account.logoutError"), "error");
    }
  }, [logout, navigate, notify, t]);

  const handleExport = useCallback(async () => {
    try {
      const json = await exportBundle();
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `securevault-keys-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      notify(t("account.exported", "Keys exported"), "success");
    } catch {
      notify(t("account.exportError", "Export failed"), "error");
    }
  }, [exportBundle, notify, t]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      localStorage.clear();
      notify(t("account.deleted", "Account deleted"), "info");
      navigate(routes.home);
    } catch {
      notify(t("account.deleteError"), "error");
    } finally {
      setDeleting(false);
    }
  }, [deleteAccount, notify, navigate, t]);

  const canDelete =
    ackChecked && confirmEmail.toLowerCase() === user?.email?.toLowerCase();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {t("account.title", "Account")}
      </Typography>
      {/* Profile info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("account.profile", "Profile")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.email", "Email")}
              </Typography>
              <Typography variant="body1">{user?.email}</Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.memberId", "Account ID")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                }}
              >
                {user?.id}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
          >
            {t("account.signOut", "Sign Out")}
          </Button>
        </CardContent>
      </Card>
      {/* Danger zone */}
      <Card sx={{ border: 2, borderColor: "error.main" }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            color="error"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("account.dangerZone", "Danger Zone")}
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              "account.deleteWarning",
              "Deleting your account is irreversible. All images and data will be permanently removed. Export your keys first.",
            )}
          </Alert>

          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            sx={{ mb: 2 }}
          >
            {t("account.exportFirst", "Export Keys Before Deletion")}
          </Button>

          <FormControlLabel
            control={
              <Checkbox
                checked={ackChecked}
                onChange={(e) => setAckChecked(e.target.checked)}
              />
            }
            label={t(
              "account.ackLabel",
              "I understand this action is permanent and all my data will be deleted",
            )}
            sx={{ display: "block", mb: 2 }}
          />

          <TextField
            fullWidth
            size="small"
            label={t("account.typeEmail", "Type your email to confirm")}
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            disabled={!ackChecked}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={!canDelete || deleting}
            onClick={handleDelete}
          >
            {deleting
              ? t("account.deletingAccount", "Deleting...")
              : t("account.deleteAccount", "Delete My Account")}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
